import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import { Close, CloudUpload } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { uploadDocumentToServer } from "../utills/uploadDocumentToServer";


export default function ProgressUpdateModal({ isOpen, onClose, schemeId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({

    updateDate: new Date().toISOString().split("T")[0],
    progressPercentage: 0,
    progressNotes: "",
    documentUrls: [],
  });


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "progressPercentage" ? parseFloat(value) : value,
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const url = await uploadDocumentToServer(file, "/ProgressUpdates");
      if (url) {
        setFormData((prev) => ({
          ...prev,
          documentUrls: [...prev.documentUrls.filter(u => u !== ""), url],
        }));
        toast.success("File uploaded successfully");
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
    }
  };



  const removeDocumentUrl = (index) => {
    const newUrls = formData.documentUrls.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, documentUrls: newUrls }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        ...formData,
        schemeId: parseInt(schemeId),
        documentUrls: formData.documentUrls.filter(url => url.trim() !== ""),
      };

      await axios.post(`${server}/scheme/progressUpdate/new`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      toast.success("Progress update added successfully");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error adding progress update:", error);
      toast.error(error.response?.data?.message || "Failed to add progress update");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">Add Progress Update</h2>
              <IconButton onClick={onClose} size="small">
                <Close />
              </IconButton>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Update Date
                </label>
                <input
                  type="date"
                  name="updateDate"
                  value={formData.updateDate}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Progress Percentage ({formData.progressPercentage}%)
                </label>
                <input
                  type="range"
                  name="progressPercentage"
                  min="0"
                  max="100"
                  step="1"
                  value={formData.progressPercentage}
                  onChange={handleChange}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Progress Notes
                </label>
                <textarea
                  name="progressNotes"
                  value={formData.progressNotes}
                  onChange={handleChange}
                  required
                  placeholder="Enter details about current progress..."
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1 flex justify-between items-center">
                  Documents
                </label>



                <div className="mb-3">
                  <label className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploading ? "bg-gray-50 border-gray-300" : "bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300"
                    }`}>
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      {uploading ? (
                        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <>
                          <CloudUpload className="text-blue-500 mb-2" />
                          <p className="text-xs text-blue-600 font-semibold">Click to upload document</p>
                        </>
                      )}
                    </div>
                    <input type="file" className="hidden" onChange={handleFileChange} disabled={uploading} />
                  </label>
                </div>

                {formData.documentUrls.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Uploaded Documents</span>
                    <div className="space-y-2">
                      {formData.documentUrls.map((url, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg group">
                          <div className="flex items-center gap-3">
                            <span className="text-xl">📄</span>
                            <div className="flex flex-col">
                              <span className="text-sm font-bold text-blue-900">Document {index + 1}</span>
                              <a href={url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline truncate max-w-[200px]">
                                {url}
                              </a>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeDocumentUrl(index)}
                            className="text-gray-400 hover:text-red-500 p-1 transition-colors"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}


              </div>


              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center ${loading ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    "Submit Update"
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
